import React, { useRef, useState } from "react";
import "./BulkImportModal.css";
import { Box } from "@mui/material";
import Title from "../title/Title";
import { PrimaryButton, SecondaryButton } from "../buttonComponent/ButtonComponent";
import ModalCloseButton from "./ModalCloseButton";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: "700px",
  minWidth: "320px",
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
  maxHeight: "90vh",
  overflow: "hidden",
};

const UpdateEmailStatusModal = ({
  isOpen,
  onClose,
  onUpdate,
  loading = false,
}) => {
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith(".csv")) {
      setValidationErrors(["Please select a CSV file (.csv)"]);
      setSelectedFile(null);
      setResult(null);
      return;
    }

    // Validate file size (max 5MB)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_FILE_SIZE) {
      setValidationErrors([`File size exceeds maximum limit of 5MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`]);
      setSelectedFile(null);
      setResult(null);
      return;
    }

    setSelectedFile(file);
    setValidationErrors([]);
    setResult(null);
  };

  const handleUpdate = async () => {
    if (!selectedFile) {
      setValidationErrors(["Please select a CSV file"]);
      return;
    }

    setResult(null);
    setValidationErrors([]);
    // Pass the file to the update handler
    const updateResult = await onUpdate(selectedFile);
    if (updateResult) {
      setResult(updateResult);
    }
    // Close the modal after the update completes (success or error)
    handleClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setValidationErrors([]);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  return (
    <div className="modal-backdrop">
      <Box sx={style}>
        <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
          <div className="modal-header-content" style={{ flexShrink: 0 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
                alignItems: "flex-start",
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1, minWidth: 0, paddingRight: "0.5rem" }}>
                <Title
                  title="Bulk Send Email for Imported Families"
                  subtitle="Send onboarding email for families that were previously imported with email sending disabled"
                  titleSize="1.125rem"
                  subtitleSize="0.875rem"
                  gap="0.25rem"
                  lineHeight="1.75rem"
                />
              </div>
              <ModalCloseButton onClick={handleClose} />
            </div>
          </div>

          <div className="bulk-import-content">
            <div className="bulk-import-instructions">
              <h3 style={{ margin: "0 0 0.75rem 0", fontSize: "0.875rem", fontWeight: 600, color: "#101828" }}>
                How This Works
              </h3>
              <div className="bulk-import-notes" style={{ marginBottom: "1.5rem" }}>
                <p style={{ margin: "0 0 0.75rem 0", fontSize: "0.75rem", color: "#475467", lineHeight: "1.25rem" }}>
                  When you import families with <strong>Send Onboarding Email</strong> turned off, the system saves their email addresses but doesn't send any emails yet. This feature lets you send those emails later.
                </p>
                <p style={{ margin: "0 0 0.75rem 0", fontSize: "0.75rem", color: "#475467", lineHeight: "1.25rem" }}>
                  Simply upload a CSV file with the email addresses of the families you want to send emails to. The system will send welcome emails with temporary passwords to those parents.
                </p>
                <p style={{ margin: "0", fontSize: "0.75rem", color: "#475467", lineHeight: "1.25rem" }}>
                  <strong>When to Use This:</strong>
                </p>
                <ul style={{ margin: "0.5rem 0 0 0", paddingLeft: "1.25rem", fontSize: "0.75rem", color: "#475467", lineHeight: "1.5rem" }}>
                  <li>You imported families but want to review them before sending emails</li>
                  <li>You only want to send emails to certain families from a large import</li>
                  <li>You need to send emails that were skipped during the original import</li>
                </ul>
              </div>

              <h3 style={{ margin: "0 0 0.75rem 0", fontSize: "0.875rem", fontWeight: 600, color: "#101828" }}>
                What You Need
              </h3>
              <div className="bulk-import-notes">
                <p style={{ margin: "0 0 0.5rem 0", fontSize: "0.75rem", color: "#475467", lineHeight: "1.25rem" }}>
                  <strong>File Requirements:</strong>
                </p>
                <ul style={{ margin: "0 0 1rem 0", paddingLeft: "1.25rem", fontSize: "0.75rem", color: "#475467", lineHeight: "1.5rem" }}>
                  <li>Use a <strong>CSV file</strong> (the same type you use for bulk import)</li>
                  <li>Make sure your file has an <strong>EMAIL</strong> column with parent email addresses</li>
                  <li>You can also include an <strong>ALTERNATE_EMAIL</strong> column if parents have a second email</li>
                  <li>You can use the same file from your bulk import, or create a new one with just the families you want</li>
                  <li>If an email isn't found or was already sent, it will be listed in the results</li>
                </ul>
                <p style={{ margin: "0", fontSize: "0.75rem", color: "#475467", lineHeight: "1.25rem" }}>
                  <strong>What's Required:</strong> Your CSV file must have an EMAIL column. The ALTERNATE_EMAIL column is optional.
                </p>
              </div>
            </div>

            <div className="bulk-import-actions">
              <SecondaryButton
                label={selectedFile ? (selectedFile.name.length > 30 ? selectedFile.name.substring(0, 30) + "..." : selectedFile.name) : "Add File"}
                width="auto"
                imageIcon={false}
                onClickFunction={handleFileSelect}
                disable={loading}
              />
            </div>

            {validationErrors.length > 0 && (
              <div className="bulk-import-errors">
                <p style={{ margin: "0 0 0.5rem 0", fontSize: "0.75rem", fontWeight: 600, color: "#D92D20" }}>
                  Validation Errors:
                </p>
                <ul style={{ margin: 0, paddingLeft: "1.25rem", fontSize: "0.75rem", color: "#D92D20" }}>
                  {validationErrors.map((error, index) => (
                    <li key={index} style={{ marginBottom: "0.25rem" }}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {selectedFile && validationErrors.length === 0 && !result && (
              <div className="bulk-import-success">
                <p style={{ margin: 0, fontSize: "0.75rem", color: "#027A48" }}>
                  ✓ CSV file selected. Ready to send emails.
                </p>
              </div>
            )}

          </div>

          <div className="button-container-delete" style={{ flexShrink: 0 }}>
            <SecondaryButton
              label="Cancel"
              width="170px"
              imageIcon={false}
              onClickFunction={handleClose}
              disable={loading}
            />
            <PrimaryButton
              disable={!selectedFile || validationErrors.length > 0 || loading}
              width="170px"
              label={loading ? "Bulk Sending..." : "Bulk Send Emails"}
              onClickFunction={handleUpdate}
              loading={loading}
            />
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </Box>
    </div>
  );
};

export default UpdateEmailStatusModal;
